import styled from 'styled-components'

const SHADOW_CARD   = '0px 0px 4px 0px rgba(43,123,199,0.25)'
const SHADOW_SEARCH = '0px 0px 20px 0px rgba(43,123,199,0.25)'

/* ── Page ─────────────────────────────────────────────────────── */

export const PageWrapper = styled.div`
  background: white;
  min-height: 100vh;
`

/* ── Header ───────────────────────────────────────────────────── */

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 2rem 2.5rem 1.25rem;
`

export const PageIconBox = styled.div`
  width: 4.0625rem;
  height: 4.0625rem;
  border-radius: 0.9375rem;
  background: linear-gradient(223deg, rgba(0,200,248,1) 16%, rgba(104,94,252,1) 78%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const PageIconImg = styled.img`
  width: 2.0625rem;
  height: 2.0625rem;
  object-fit: contain;
  filter: brightness(0) invert(1);
`

export const PageTitle = styled.h1`
  font-family: Poppins;
  font-size: 2rem;
  font-weight: 600;
  color: #000000;
  margin: 0;
  line-height: 1.15;
`

export const PageSubtitle = styled.p`
  font-family: Poppins;
  font-size: 1rem;
  font-weight: 400;
  color: #000000;
  opacity: 0.55;
  margin: 0;
`

/* ── Filter bar (same as /orders admin) ──────────────────────── */

export const FilterArea = styled.div`
  padding: 0 2.5rem 1.25rem;
`

export const FilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
  background: #EFF0F6;
  border-radius: 10px;
  padding: 0.625rem 0.75rem;
`

export const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const FilterFieldLabel = styled.div`
  font-family: Poppins;
  font-size: 0.72rem;
  font-weight: 600;
  color: #5a6170;
`

export const FilterSearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  & img {
    position: absolute;
    left: 0.55rem;
    height: 0.72rem;
    opacity: 0.4;
    z-index: 1;
    pointer-events: none;
  }
  & input {
    height: 2rem;
    width: 16rem;
    border: 1px solid #D8D8D8;
    border-radius: 6px;
    background: white;
    font-family: Poppins;
    font-size: 0.8rem;
    color: #1A1A2E;
    padding: 0 0.75rem 0 1.75rem;
    outline: none;
    &::placeholder { color: #B1B8C5; }
    &:focus { border-color: #4465FE; }
  }
`

/* ── Table area ───────────────────────────────────────────────── */

export const TableArea = styled.div`
  padding: 0 2.5rem 3rem;
`

export const TableCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: ${SHADOW_CARD};
  overflow: hidden;
`

export const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  font-family: Poppins;
  font-size: 0.9375rem;
  font-weight: 400;
  color: #BABABA;
`

/* ── Agency Block (Accordion) ──────────────────────────────────── */

export const AgencyBlock = styled.div`
  border-bottom: 1px solid #F0F2F8;
  &:last-child { border-bottom: none; }
`

export const AgencyRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  gap: 0.875rem;
  cursor: pointer;
  background: ${p => p.$open ? '#F6F7FE' : 'white'};
  transition: background 0.15s;
  &:hover { background: #F6F7FE; }
`

export const ArrowBtn = styled.div`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: ${p => p.$open ? '#EEF2FF' : '#F3F4F6'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$open ? '#4F46E5' : '#9CA3AF'};
  font-size: 0.625rem;
  transform: rotate(${p => p.$open ? '90deg' : '0deg'});
  transition: transform 0.2s ease, background 0.15s, color 0.15s;
  flex-shrink: 0;
`

export const AgencyNameText = styled.span`
  font-family: Poppins;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1A1A2E;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const AgencyStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

export const StatPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.75rem;
  border-radius: 100px;
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${
    p => p.$type === 'price'     ? '#F0FDF4' :
         p.$type === 'completed' ? '#F5F3FF' :
         '#EEF2FF'
  };
  color: ${
    p => p.$type === 'price'     ? '#16A34A' :
         p.$type === 'completed' ? '#7C3AED' :
         '#4F46E5'
  };
  border: 1px solid ${
    p => p.$type === 'price'     ? '#BBF7D0' :
         p.$type === 'completed' ? '#DDD6FE' :
         '#C7D2FE'
  };
`

/* ── Formation Cards (Formations list) ────────────────────────── */

export const FormationCardsWrapper = styled.div`
  overflow: hidden;
  max-height: ${p => p.$open ? '9999px' : '0'};
  transition: max-height ${p => p.$open ? '0.25s ease-in' : '0.15s ease-out'};
`

export const FormationCardsInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const FormationCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 0.75rem 1.25rem 0.75rem 2.75rem;
  border-bottom: 1px solid #F0F2F8;
  background: #F8F9FC;
  transition: background 0.1s;
  &:last-child { border-bottom: none; }
  &:hover { background: #F0F2FA; }
`

export const FormationLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #E0E3F0;
  flex-shrink: 0;
  overflow: hidden;
`

export const FormationLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

export const FormationInfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
`

export const FormationCategory = styled.span`
  font-family: Poppins;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${p => p.color || '#4F46E5'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

export const FormationName = styled.span`
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #1A1A2E;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const FormationQtyBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: right;
  white-space: nowrap;
`

export const FormationQtyLine = styled.span`
  font-family: Poppins;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #6B7280;
`

export const FormationSpacer = styled.div`
  flex: 0 0 auto;
`

export const FilterActionBtn = styled.button`
  height: 2rem;
  padding: 0 1rem;
  background: #4465FE;
  border: none;
  border-radius: 6px;
  font-family: Poppins;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: background 0.15s;
  & img { width: 0.875rem; height: 0.875rem; object-fit: contain; filter: brightness(0) invert(1); }
  &:hover:not(:disabled) { background: #3352e0; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export const FilterResetBtn = styled.button`
  height: 2rem;
  padding: 0 0.75rem;
  background: white;
  border: 1px solid #D8D8D8;
  border-radius: 6px;
  font-family: Poppins;
  font-size: 0.8rem;
  color: #6B7280;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  &:hover { border-color: #9CA3AF; }
`
